import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Popover } from '@mui/material';
import { Folder, LogOut } from 'lucide-react';

import { clearUser } from '../../redux/actions';
import { selectUser } from '../../redux/selectors';
import { tokenStorage } from '../../utils';
import { globalConfirm } from '../GlobalSnackbar';

const initialsOf = (user: any): string => {
  if (!user) return '?';
  const a = user.firstName?.[0];
  const b = user.lastName?.[0];
  if (a && b) return (a + b).toUpperCase();
  if (a) return a.toUpperCase();
  if (user.email) return user.email[0].toUpperCase();
  return '?';
};

const fullName = (user: any): string => {
  if (!user) return '';
  if (user.firstName) return `${user.firstName} ${user.lastName ?? ''}`.trim();
  return user.email ?? '';
};

const UserMenu = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  const close = () => setOpen(false);

  const handleLogout = () => {
    close();
    globalConfirm({
      title: 'Выход из системы',
      message: 'Вы уверены, что хотите выйти?',
      confirmText: 'Выйти',
      cancelText: 'Отмена',
      onConfirm: () => {
        tokenStorage.remove();
        localStorage.removeItem('selected_role_id');
        localStorage.removeItem('current_project_id');
        dispatch(clearUser());
        navigate('/login');
      },
      onCancel: () => undefined,
    });
  };

  const item = (label: string, icon: React.ReactNode, onClick: () => void, danger = false) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        height: 32,
        padding: '0 10px',
        border: 'none',
        background: 'transparent',
        borderRadius: 'var(--shell-radius-sm)',
        cursor: 'pointer',
        textAlign: 'left',
        fontSize: 13,
        color: danger ? 'var(--shell-semantic-error)' : 'var(--shell-text-primary)',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'var(--shell-bg-hover)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      <span style={{ display: 'inline-flex', color: 'var(--shell-text-tertiary)' }}>{icon}</span>
      {label}
    </button>
  );

  return (
    <>
      <button
        type="button"
        ref={anchorRef}
        onClick={() => setOpen(true)}
        title={fullName(user) || 'Профиль'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          borderRadius: '50%',
          border: '1px solid var(--shell-border-subtle)',
          background: 'var(--shell-bg-subtle)',
          color: 'var(--shell-text-primary)',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'background var(--shell-transition-fast)',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.background = 'var(--shell-bg-hover)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.background = 'var(--shell-bg-subtle)';
        }}
      >
        {initialsOf(user)}
      </button>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: 240,
              padding: '6px',
              border: '1px solid var(--shell-border-subtle)',
              boxShadow: 'var(--shell-shadow-overlay)',
              borderRadius: 'var(--shell-radius-lg)',
              background: 'var(--shell-bg-surface)',
            },
          },
        }}
      >
        <div
          style={{
            padding: '8px 10px 10px',
            borderBottom: '1px solid var(--shell-border-subtle)',
            marginBottom: 6,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--shell-text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {fullName(user) || '—'}
          </div>
          {user?.email && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--shell-text-tertiary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.email}
            </div>
          )}
        </div>
        {item('Проекты', <Folder size={14} strokeWidth={1.75} />, () => {
          close();
          navigate('/projects');
        })}
        {item('Выйти', <LogOut size={14} strokeWidth={1.75} />, handleLogout, true)}
      </Popover>
    </>
  );
};

export default UserMenu;
